import React from "react";
import LegalPage from "./LegalPage";
import useContactInfo from "./useContactInfo";

export default function Privacy() {
  const { contactEmail, privacyEmail } = useContactInfo();
  return (
    <LegalPage testId="privacy-page" title="Privacy Policy" updated="February 9, 2026">
      <p>
        This Privacy Policy explains what data ZA Player Pro Web (&ldquo;the app&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;) processes when you install the client on your phone, tablet, TV, computer, or streaming device, and when you use the marketing website at <a href="https://zaplayerpro.club">zaplayerpro.club</a>.
      </p>
      <p>
        <strong>Short version:</strong> the app is a player only. We do not collect personal data from it. Your playlists, watch history, favourites and profile choices stay on your device. Payments are handled by the payment provider you pick — we never see your card details.
      </p>

      <h2>1. What the client app collects</h2>
      <p><strong>Nothing that identifies you personally.</strong> The client app runs entirely on your device. It does not embed any third-party analytics SDK, advertising SDK, or crash reporter that phones home to us or to any third party.</p>
      <ul>
        <li>Your <strong>M3U or Xtream credentials</strong> stay in local storage. They are used only to fetch media from the origin you configured, never sent to us.</li>
        <li>Your <strong>watch history, favourites, resume points and profile PINs</strong> live in local storage / IndexedDB / on-device SQLite. They never leave the device.</li>
        <li>The <strong>device MAC address</strong> on the Activation screen binds your paid key to that device. It is never associated with your name, email, or any tracking identifier.</li>
        <li>The app makes network requests to the <strong>media origins you configured yourself</strong>. We are not a party to those requests.</li>
      </ul>

      <h2>2. What the marketing website collects</h2>
      <ul>
        <li>When you claim a <strong>free 5-day trial</strong> we store the device MAC address, a randomised activation key and the timestamp.</li>
        <li>When you <strong>buy or renew</strong> we store the payment reference, plan, device MAC and amount to reconcile the payment. We never see card details.</li>
        <li>When you use the <strong>self-service playlist manager</strong> we store the M3U or Xtream URL attached to your activation key.</li>
        <li><strong>Web server logs</strong> (IP, user agent, timestamp, path) are kept for up to 30 days for abuse prevention and debugging.</li>
      </ul>
      <p>The website does not use tracking cookies or third-party advertising pixels. A Cloudflare Turnstile challenge appears on the trial page for spam prevention — see <a href="https://www.cloudflare.com/privacypolicy/">Cloudflare&rsquo;s privacy policy</a>.</p>

      <h2>3. Payments</h2>
      <p>We accept payments through <strong>PayFast, PayPal, Stripe,</strong> or (on iOS/tvOS) <strong>Apple StoreKit</strong>. The provider — not us — handles card details. We receive only a payment reference, amount, currency, and status.</p>
      <ul>
        <li><a href="https://payfast.io/legal/privacy-policy">PayFast</a></li>
        <li><a href="https://www.paypal.com/webapps/mpp/ua/privacy-full">PayPal</a></li>
        <li><a href="https://stripe.com/privacy">Stripe</a></li>
        <li><a href="https://www.apple.com/legal/privacy/">Apple</a> (iOS/tvOS in-app purchases)</li>
      </ul>

      <h2>4. Support and contact requests</h2>
      <p>When you email <a href={`mailto:${contactEmail}`}>{contactEmail}</a> we store your email address and the message contents for as long as the ticket takes to resolve. Resolved tickets are kept for up to 12 months.</p>

      <h2>5. What we do NOT do</h2>
      <ul>
        <li>We do not sell your data. Ever.</li>
        <li>We do not rent, share, or transfer your data to advertising networks or data brokers.</li>
        <li>We do not track you across other apps or websites.</li>
        <li>We do not build behavioural profiles of you.</li>
        <li>We do not host, index, promote, or resell any third-party media. This is a player only.</li>
      </ul>

      <h2>6. Your rights</h2>
      <p>Under POPIA (South Africa), the GDPR (EU/UK) and the CCPA (California) you can:</p>
      <ul>
        <li>Ask what information we hold about you.</li>
        <li>Ask us to correct inaccurate information.</li>
        <li>Ask us to delete information we hold (subject to legal record-keeping).</li>
        <li>Request a machine-readable copy of your data.</li>
        <li>Withdraw consent.</li>
        <li>Lodge a complaint with your local data protection authority.</li>
      </ul>
      <p>Email <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a> from your registered address. We reply within 14 days.</p>

      <h2>7. Children</h2>
      <p>The app is rated 4+ but is intended for a general audience. We do not knowingly collect personal information from anyone under 13.</p>

      <h2>8. Security</h2>
      <p>The website is served over HTTPS with modern TLS ciphers. Our database is not exposed to the public internet. Access to personal data is limited and logged.</p>

      <h2>9. Changes to this policy</h2>
      <p>Material changes will be surfaced in the app on next launch. The date at the top of this page always reflects the most recent substantive change.</p>

      <h2>10. Contact</h2>
      <p>Privacy: <a href={`mailto:${privacyEmail}`}>{privacyEmail}</a>. Support: <a href={`mailto:${contactEmail}`}>{contactEmail}</a> or <a href="/support">/support</a>.</p>
    </LegalPage>
  );
}
